/**
 * UnifiedTimelineTab — timeline polimórfica activity + task (UX-2 / BUG-V2-022).
 *
 * Stub criado em UX-1; implementação real entregue em UX-2 conforme spec
 * "merge timeline + tasks". Endpoint: GET /api/v1/cards/:type/:id/timeline.
 *
 * Quando UX-2 implementar:
 *   - Backend retorna entries[] polimórficos { id, type, criado_em_iso, autor, payload }
 *   - Render distingue 'activity' (compat layout existente) vs 'task' (horário + sync)
 *   - Filtro tudo/só atividades/só tarefas
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert, Badge } from '@/components/ui';
import { api, hasToken } from '@/lib/api';

import { formatarData, formatTime } from '@/lib/format-date';
export type CardType = 'deal' | 'service_order';

export interface TimelineEntry {
  id: string;
  type: 'activity' | 'task';
  criadoEmIso: string;
  autor: { id: string; nome: string; fotoUrl?: string | null } | null;
  payload: Record<string, unknown>;
}

interface UnifiedTimelineTabProps {
  cardId: string;
  workflowSlug: string;
  cardType: CardType;
}

type FilterMode = 'all' | 'activity' | 'task';

export function UnifiedTimelineTab({ cardId, cardType }: UnifiedTimelineTabProps) {
  const [filter, setFilter] = useState<FilterMode>('all');

  const { data: entries = [], isLoading, isError, error } = useQuery<TimelineEntry[]>({
    queryKey: ['v1', 'cards', cardType, cardId, 'timeline'],
    queryFn: () =>
      api.get<TimelineEntry[]>(`/api/v1/cards/${cardType}/${encodeURIComponent(cardId)}/timeline`),
    enabled: hasToken() && !!cardId,
    staleTime: 15_000,
  });

  const filtered = filter === 'all' ? entries : entries.filter((e) => e.type === filter);

  return (
    <div data-testid="unified-timeline" className="space-y-3">
      <div className="flex items-center gap-2" role="tablist" aria-label="Filtro timeline">
        <FilterButton current={filter} value="all" onChange={setFilter}>
          Tudo ({entries.length})
        </FilterButton>
        <FilterButton current={filter} value="activity" onChange={setFilter}>
          Só atividades
        </FilterButton>
        <FilterButton current={filter} value="task" onChange={setFilter}>
          Só tarefas
        </FilterButton>
      </div>

      {isLoading && (
        <p
          data-testid="timeline-loading"
          className="text-sm text-ink-3 dark:text-ink-dark-3 italic py-4"
        >
          Carregando timeline…
        </p>
      )}

      {isError && (
        <Alert variant="danger" rail data-testid="timeline-error">
          Erro ao carregar timeline:{' '}
          {error instanceof Error ? error.message : 'desconhecido'}
        </Alert>
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <p
          data-testid="timeline-empty"
          className="text-sm text-ink-3 dark:text-ink-dark-3 italic py-6 text-center"
        >
          Nenhuma entrada na timeline ainda.
        </p>
      )}

      {!isLoading && filtered.length > 0 && (
        <ul data-testid="timeline-list" className="space-y-2">
          {filtered.map((e) => (
            <TimelineRow key={e.id} entry={e} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterButton({
  current,
  value,
  onChange,
  children,
}: {
  current: FilterMode;
  value: FilterMode;
  onChange: (m: FilterMode) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      role="tab"
      aria-selected={active}
      data-testid={`timeline-filter-${value}`}
      className={
        active
          ? 'text-sm font-medium px-3 py-1.5 rounded-md bg-red text-white'
          : 'text-sm px-3 py-1.5 rounded-md text-ink-2 dark:text-ink-dark-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm'
      }
    >
      {children}
    </button>
  );
}

function formatRelative(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface TaskInvitee {
  userId: string;
  nome: string;
  status: string;
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  const payload = entry.payload as {
    tipo?: string;
    descricao?: string;
    titulo?: string;
    text?: string;
    startAtIso?: string;
    endAtIso?: string;
    syncStatus?: 'synced' | 'failed' | 'pending';
    field?: string;
    from?: unknown;
    to?: unknown;
    invitees?: TaskInvitee[];
  };

  const isTask = entry.type === 'task';
  const icon = isTask ? '✅' : '📝';
  const titulo =
    payload.titulo ?? payload.text ?? payload.descricao ?? (payload.field ? `Alterou ${payload.field}` : entry.type);

  const invitees = Array.isArray(payload.invitees) ? payload.invitees : [];

  return (
    <li
      data-testid={`timeline-entry-${entry.id}`}
      className="flex gap-3 p-3 rounded-xl border border-line dark:border-line-dark"
    >
      <span className="text-lg flex-shrink-0" aria-hidden="true">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate">
            {String(titulo)}
          </span>
          {isTask && payload.startAtIso && (
            <Badge variant="info" size="sm">
              📅 {formatRelative(payload.startAtIso)}
              {payload.endAtIso && ` – ${formatTime(payload.endAtIso)}`}
            </Badge>
          )}
          {isTask && payload.syncStatus === 'synced' && (
            <Badge variant="success" size="sm" title="Sincronizado com Google Calendar">📅 sincronizado</Badge>
          )}
          {isTask && payload.syncStatus === 'failed' && (
            <Badge variant="danger" size="sm">⚠️ sync falhou</Badge>
          )}
        </div>
        <div className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
          {entry.autor?.nome ?? 'sistema'} · {formatRelative(entry.criadoEmIso)}
        </div>
        {payload.descricao && payload.titulo && (
          <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-1 whitespace-pre-wrap">
            {payload.descricao}
          </p>
        )}
        {/* UI-3 / TD-V2-PERM-07: convidados da task com ícone por status. */}
        {isTask && invitees.length > 0 && (
          <div
            data-testid={`timeline-invitees-${entry.id}`}
            className="flex items-center gap-1 mt-1.5 flex-wrap"
          >
            <span className="text-xs text-ink-3 dark:text-ink-dark-3 mr-1">
              Convidados:
            </span>
            {invitees.map((inv) => (
              <Badge
                key={inv.userId}
                variant={
                  inv.status === 'aceito'
                    ? 'success'
                    : inv.status === 'recusado'
                      ? 'danger'
                      : 'warning'
                }
                size="sm"
                title={`Status: ${inv.status}`}
                data-testid={`timeline-invitee-${entry.id}-${inv.userId}`}
              >
                {inv.status === 'aceito' ? '✓' : inv.status === 'recusado' ? '✕' : '⏳'}{' '}
                {inv.nome}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}
